/**
 * Server-tool web-search result block. The SDK surfaces these as
 * provenance under the assistant's response; ``encrypted_content``
 * must be preserved verbatim if we re-send, but the renderer only
 * needs ``title``/``url``. Styled as a bordered card.
 */

import { Globe } from 'lucide-react';
import type { WebSearchToolResultBlock } from '@/lib/agent/types';
import { cn } from '@/lib/utils/cn';

export interface WebSearchResultViewProps {
  block: WebSearchToolResultBlock;
  className?: string;
}

export function WebSearchResultView({ block, className }: WebSearchResultViewProps) {
  const items = block.content ?? [];
  if (items.length === 0) return null;
  return (
    <div
      className={cn(
        'my-1.5 select-text rounded-md border border-sky-200/70 bg-sky-50/40 dark:border-sky-500/20 dark:bg-sky-500/[0.06]',
        className
      )}
    >
      <div className="flex items-center gap-1.5 border-sky-200/70 border-b px-3 py-1.5 font-medium text-[11px] text-sky-700 uppercase tracking-wider dark:border-sky-500/20 dark:text-sky-300">
        <Globe className="size-3" />
        Web search · {items.length} result{items.length === 1 ? '' : 's'}
      </div>
      <ol className="space-y-1 px-3 py-2">
        {items.map((item, i) => (
          <li key={`${item.url}-${i}`} className="text-[12.5px]">
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block truncate font-medium text-sky-700 hover:underline dark:text-sky-300"
            >
              {item.title}
            </a>
            <div className="truncate text-[11px] text-gray-500 dark:text-gray-400">
              {item.url}
              {item.page_age && <span className="ml-1.5 italic">· {item.page_age}</span>}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
